import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import mlcontour from 'maplibre-contour';
import type { DemSource } from 'maplibre-contour/dist/dem-source';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { RouteResponse, ComputedRoute } from '../types/api';

interface RouteMapProps {
  routes: RouteResponse | null;
  selectedRoute: number;
  onMapClick?: (lat: number, lon: number) => void;
  origin: { lat: number; lon: number } | null;
  destination: { lat: number; lon: number } | null;
  centerOn?: { lat: number; lon: number } | null;
  // What metric to color the highlighted route by: 'hills' (segment grade) or
  // 'safety' (segment stress). Lets the user sanity-check the router's scoring.
  colorBy: 'hills' | 'safety';
  // Show the edges the router explored (relaxed) as a faint layer.
  showExplored: boolean;
}

type MapStyle = 'topo' | 'street';

const DEM_URL = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';

const DEFAULT_CENTER: [number, number] = [44.98, -93.27];
const DEFAULT_ZOOM = 12;

// Grayscale for non-highlighted routes: thin, muted, so the highlighted one pops.
const GRAY = '#9a9a9a';
const GRAY_OUTLINE = '#000';

// Per-segment color stops for the highlighted route. Green = good, red = bad.
// Hills: grade %, 0% green -> 12%+ red.
// Safety: stress 0 green -> 5 red (inverted so low stress = green = safe).
function gradeColor(t: number): string {
  // t in [0,1]; 0 green, 1 red
  const r = Math.round(40 + 215 * t);
  const g = Math.round(180 - 130 * t);
  const b = Math.round(60 - 40 * t);
  return `rgb(${r},${g},${b})`;
}

// MapLibre line-gradient uses a step expression over `line-progress` [0..1].
// We embed per-segment values as a properties array on the feature and build an
// interpolate expression that samples the gradient at each segment boundary.
function buildGradientExpression(values: number[], max: number): any {
  // Produce a 'interpolate' expression over line-progress mapping each progress
  // fraction to a color computed from the normalized segment value.
  const stops: any[] = [];
  const n = values.length;
  if (n === 0) {
    return GRAY;
  }
  for (let i = 0; i < n; i++) {
    const progress = n === 1 ? 0.5 : i / (n - 1);
    const t = Math.min(1, Math.max(0, values[i] / max));
    stops.push(progress, gradeColor(t));
  }
  return ['interpolate', ['linear'], ['line-progress'], ...stops];
}

function buildSafetyGradientExpression(values: number[]): any {
  // stress: 0 = safe (green), 5 = unsafe (red). Invert so low stress -> green.
  const stops: any[] = [];
  const n = values.length;
  if (n === 0) return GRAY;
  const MAX_STRESS = 5.0;
  for (let i = 0; i < n; i++) {
    const progress = n === 1 ? 0.5 : i / (n - 1);
    const t = Math.min(1, Math.max(0, values[i] / MAX_STRESS));
    stops.push(progress, gradeColor(t));
  }
  return ['interpolate', ['linear'], ['line-progress'], ...stops];
}

function createTopoStyle(demSource: DemSource): maplibregl.StyleSpecification {
  return {
    version: 8,
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
    sources: {
      'osm-tiles': {
        type: 'raster',
        tiles: [
          'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
        ],
        tileSize: 256,
        attribution: '&copy; OpenStreetMap contributors',
        maxzoom: 19,
      },
      'hillshade': {
        type: 'raster-dem',
        tiles: [demSource.sharedDemProtocolUrl],
        encoding: 'terrarium',
        tileSize: 256,
        maxzoom: 15,
      },
      'contours': {
        type: 'vector',
        tiles: [
          demSource.contourProtocolUrl({
            multiplier: 3.28084,
            overzoom: 1,
            thresholds: {
              11: [200, 1000],
              12: [100, 500],
              13: [50, 200],
              14: [20, 100],
              15: [10, 50],
            },
            elevationKey: 'ele',
            levelKey: 'level',
            contourLayer: 'contours',
          }),
        ],
        maxzoom: 15,
      },
    },
    layers: [
      {
        id: 'osm-tiles-layer',
        type: 'raster',
        source: 'osm-tiles',
        minzoom: 0,
        maxzoom: 19,
        // Slight transparency so hillshade shows through the base raster.
        paint: { 'raster-opacity': 0.85 },
      },
      {
        id: 'hillshading',
        type: 'hillshade',
        source: 'hillshade',
        layout: { visibility: 'visible' },
        paint: {
          'hillshade-shadow-color': '#2a2a2a',
          'hillshade-highlight-color': '#fff',
          'hillshade-accent-color': '#777',
          // Bumped from 0.3 to make terrain more visible per user request.
          'hillshade-exaggeration': 0.7,
        },
      },
      {
        id: 'contour-lines',
        type: 'line',
        source: 'contours',
        'source-layer': 'contours',
        paint: {
          'line-color': '#5a3e2b',
          'line-opacity': 0.55,
          'line-width': ['match', ['get', 'level'], 1, 1.3, 0.55],
        },
      },
      {
        id: 'contour-labels',
        type: 'symbol',
        source: 'contours',
        'source-layer': 'contours',
        filter: ['>', ['get', 'level'], 0],
        paint: {
          'text-halo-color': '#f5f0e8',
          'text-halo-width': 1.5,
        },
        layout: {
          'symbol-placement': 'line',
          'text-size': 10,
          'text-field': ["concat", ["number-format", ["get", "ele"], {}], "'"],
          'text-font': ['Noto Sans Bold'],
        },
      },
    ],
  };
}

function createStreetStyle(): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {
      'osm-tiles': {
        type: 'raster',
        tiles: [
          'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
        ],
        tileSize: 256,
        attribution: '&copy; OpenStreetMap contributors',
      },
    },
    layers: [
      {
        id: 'osm-tiles-layer',
        type: 'raster',
        source: 'osm-tiles',
        minzoom: 0,
        maxzoom: 19,
      },
    ],
  };
}

function fmtDist(m: number): string {
  return (m / 1609.344).toFixed(2) + ' mi';
}
function fmtElev(m: number): string {
  return (m * 3.28084).toFixed(0) + ' ft';
}

export function RouteMap({ routes, selectedRoute, onMapClick, origin, destination, centerOn, colorBy, showExplored }: RouteMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const demSourceRef = useRef<DemSource | null>(null);
  const [lng, setLng] = useState(DEFAULT_CENTER[1]);
  const [lat, setLat] = useState(DEFAULT_CENTER[0]);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [mapStyle, setMapStyle] = useState<MapStyle>('topo');
  const routeSourcesAdded = useRef<Set<string>>(new Set());
  const markersAdded = useRef(false);
  const onMapClickRef = useRef(onMapClick);
  onMapClickRef.current = onMapClick;
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const hoveredRef = useRef<number | null>(null);
  // Per-layer route metadata + hover listeners so we can pass stable functions
  // to map.on/off (which require the same function reference to deregister).
  const routeMetaRef = useRef<Map<string, { idx: number; route: ComputedRoute }>>(new Map());
  const hoverEnterHandlers = useRef<Map<string, (e: maplibregl.MapMouseEvent) => void>>(new Map());
  const hoverLeaveHandlers = useRef<Map<string, () => void>>(new Map());

  useEffect(() => {
    if (!mapContainer.current) return;

    const demSource = new mlcontour.DemSource({
      url: DEM_URL,
      encoding: 'terrarium',
      maxzoom: 15,
      worker: true,
    });
    demSource.setupMaplibre(maplibregl);
    demSourceRef.current = demSource;

    const style = mapStyle === 'topo' ? createTopoStyle(demSource) : createStreetStyle();

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style,
      center: [lng, lat],
      zoom: zoom,
    });

    popupRef.current = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 12,
    });

    map.current.on('move', () => {
      if (!map.current) return;
      setLng(Number(map.current.getCenter().lng.toFixed(4)));
      setLat(Number(map.current.getCenter().lat.toFixed(4)));
      setZoom(Number(map.current.getZoom().toFixed(2)));
    });

    map.current.on('click', (e: maplibregl.MapMouseEvent) => {
      if (onMapClickRef.current) {
        onMapClickRef.current(e.lngLat.lat, e.lngLat.lng);
      }
    });

    return () => {
      map.current?.remove();
      map.current = null;
      demSourceRef.current = null;
      routeSourcesAdded.current.clear();
      markersAdded.current = false;
      hoveredRef.current = null;
    };
    // eslint-disable-next-line
  }, [mapStyle]);

  const switchStyle = (style: MapStyle) => {
    if (style === mapStyle) return;
    routeSourcesAdded.current.clear();
    markersAdded.current = false;
    setMapStyle(style);
  };

  // Update route layers when routes or selection/hover/colorBy change.
  useEffect(() => {
    if (!map.current || !routes) return;

    const m = map.current;

    const updateRoutes = () => {
      if (!m || !routes) return;

      // --- Explored-edges layer (faint, shows what the router considered) ---
      const exploredSourceId = 'explored-edges';
      const exploredLayerId = 'explored-edges-layer';
      const allExplored = showExplored
        ? routes.routes.flatMap((r) => r.explored_geometry || [])
        : [];
      const exploredFeatures: GeoJSON.Feature[] = allExplored.map((seg) => ({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [[seg[0][1], seg[0][0]], [seg[1][1], seg[1][0]]] },
        properties: {},
      }));

      if (showExplored && exploredFeatures.length > 0) {
        if (m.getSource(exploredSourceId)) {
          (m.getSource(exploredSourceId) as maplibregl.GeoJSONSource).setData({
            type: 'FeatureCollection',
            features: exploredFeatures,
          });
        } else {
          m.addSource(exploredSourceId, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: exploredFeatures },
          });
          m.addLayer({
            id: exploredLayerId,
            type: 'line',
            source: exploredSourceId,
            paint: {
              'line-color': '#6688aa',
              'line-width': 0.6,
              'line-opacity': 0.25,
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
          });
        }
        if (m.getLayer(exploredLayerId)) {
          m.setLayoutProperty(exploredLayerId, 'visibility', 'visible');
        }
      } else if (m.getLayer(exploredLayerId)) {
        m.setLayoutProperty(exploredLayerId, 'visibility', 'none');
      }

      // --- Per-route layers ---
      // Highlighted = selected OR hovered (hover wins if present). All others gray.
      const highlightedIdx = hoveredRef.current ?? selectedRoute;

      routes.routes.forEach((route, idx) => {
        const sourceId = `route-${route.label}-${idx}`;
        const layerId = `route-layer-${route.label}-${idx}`;
        const outlineId = `route-outline-${route.label}-${idx}`;

        const coords: [number, number][] = route.geometry.map(
          ([lat, lon]) => [lon, lat]
        );

        const isHighlighted = idx === highlightedIdx;

        // Feature properties carry per-segment values for the gradient.
        const segValues = colorBy === 'hills' ? route.segment_grades : route.segment_stress;
        const gradientExpr =
          colorBy === 'hills'
            ? buildGradientExpression(segValues, 12.0)
            : buildSafetyGradientExpression(segValues);

        const featureData = {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: coords },
          properties: { idx, label: route.label },
        };

        if (m.getSource(sourceId)) {
          (m.getSource(sourceId) as maplibregl.GeoJSONSource).setData(featureData as any);
        } else {
          m.addSource(sourceId, { type: 'geojson', data: featureData as any });

          m.addLayer({
            id: outlineId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': GRAY_OUTLINE,
              'line-width': isHighlighted ? 8 : 4,
              'line-opacity': isHighlighted ? 0.4 : 0.12,
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
          });

          m.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': GRAY,
              'line-width': isHighlighted ? 6 : 2,
              'line-opacity': isHighlighted ? 1.0 : 0.5,
              'line-gradient': gradientExpr,
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
          });

          routeSourcesAdded.current.add(sourceId);
        }

        // Update paint properties for highlight state changes.
        if (m.getLayer(outlineId)) {
          m.setPaintProperty(outlineId, 'line-width', isHighlighted ? 8 : 4);
          m.setPaintProperty(outlineId, 'line-opacity', isHighlighted ? 0.4 : 0.12);
          m.setPaintProperty(outlineId, 'line-color', isHighlighted ? GRAY_OUTLINE : GRAY_OUTLINE);
        }
        if (m.getLayer(layerId)) {
          m.setPaintProperty(layerId, 'line-width', isHighlighted ? 6 : 2);
          m.setPaintProperty(layerId, 'line-opacity', isHighlighted ? 1.0 : 0.5);
          // Recompute gradient each time in case colorBy or selection changed.
          m.setPaintProperty(layerId, 'line-gradient', gradientExpr);
          m.setLayoutProperty(layerId, 'visibility', 'visible');
        }

        // Hover handlers: highlight + tooltip with distance/safety/hills.
        // Store route metadata in a ref keyed by layer id so the single shared
        // listener can look up which route was hovered without re-binding.
        routeMetaRef.current.set(layerId, { idx, route });
        const enter = hoverEnterHandlers.current.get(layerId);
        const leave = hoverLeaveHandlers.current.get(layerId);
        if (enter) m.off('mouseenter', layerId, enter);
        if (leave) m.off('mouseleave', layerId, leave);
        const newEnter = makeEnter(layerId);
        const newLeave = makeLeave(layerId);
        hoverEnterHandlers.current.set(layerId, newEnter);
        hoverLeaveHandlers.current.set(layerId, newLeave);
        m.on('mouseenter', layerId, newEnter);
        m.on('mouseleave', layerId, newLeave);
      });

      const allCoords: [number, number][] = routes.routes.flatMap((r) =>
        r.geometry.map(([lat, lon]) => [lon, lat] as [number, number])
      );
      if (allCoords.length >= 2) {
        const bounds = new maplibregl.LngLatBounds(allCoords[0], allCoords[0]);
        allCoords.forEach((c) => bounds.extend(c));
        m.fitBounds(bounds, { padding: 60, maxZoom: 16, duration: 1000 });
      }
    };

    // Shared hover enter/leave factories. They read route metadata from the ref
    // so we pass a stable listener function to m.on (which requires it).
    const makeEnter = (layerId: string) => (e: maplibregl.MapMouseEvent) => {
      const meta = routeMetaRef.current.get(layerId);
      const m = map.current;
      if (!meta || !m || !popupRef.current) return;
      const { idx, route } = meta;
      const s = route.stats;
      const html = `
        <div style="font-size:12px;line-height:1.4">
          <strong>${route.label}</strong><br/>
          ${fmtDist(s.distance_m)}<br/>
          Safety ${s.safety_score.toFixed(0)}/100<br/>
          Gain ${fmtElev(s.elevation_gain_m)}<br/>
          Max grade ${s.max_grade_pct.toFixed(1)}%
        </div>`;
      popupRef.current.setLngLat(e.lngLat).setHTML(html).addTo(m);
      hoveredRef.current = idx;
      const lid = `route-layer-${route.label}-${idx}`;
      if (m.getLayer(lid)) {
        m.setPaintProperty(lid, 'line-width', 6);
        m.setPaintProperty(lid, 'line-opacity', 1.0);
      }
      m.getCanvas().style.cursor = 'pointer';
    };
    const makeLeave = (layerId: string) => () => {
      const meta = routeMetaRef.current.get(layerId);
      const m = map.current;
      if (!meta || !m) return;
      const { idx, route } = meta;
      if (popupRef.current) popupRef.current.remove();
      hoveredRef.current = null;
      const lid = `route-layer-${route.label}-${idx}`;
      if (m.getLayer(lid)) {
        const isSel = idx === selectedRoute;
        m.setPaintProperty(lid, 'line-width', isSel ? 6 : 2);
        m.setPaintProperty(lid, 'line-opacity', isSel ? 1.0 : 0.5);
      }
      m.getCanvas().style.cursor = '';
    };

    if (m.loaded()) {
      updateRoutes();
    } else {
      m.once('load', updateRoutes);
    }
  }, [routes, selectedRoute, colorBy, showExplored]);

  // Update origin/destination markers
  useEffect(() => {
    if (!map.current) return;
    const m = map.current;

    const updateMarkers = () => {
      if (!m) return;

      const markerSourceId = 'od-markers';
      const markerLayerId = 'od-markers-layer';

      const features: GeoJSON.Feature[] = [];
      if (origin) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [origin.lon, origin.lat] },
          properties: { type: 'origin' },
        });
      }
      if (destination) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [destination.lon, destination.lat] },
          properties: { type: 'destination' },
        });
      }

      if (features.length === 0) return;

      const geojson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features,
      };

      if (m.getSource(markerSourceId)) {
        (m.getSource(markerSourceId) as maplibregl.GeoJSONSource).setData(geojson);
      } else {
        m.addSource(markerSourceId, { type: 'geojson', data: geojson });
        m.addLayer({
          id: markerLayerId,
          type: 'circle',
          source: markerSourceId,
          paint: {
            'circle-radius': 8,
            'circle-color': [
              'case',
              ['==', ['get', 'type'], 'origin'],
              '#22c55e',
              '#e94560',
            ],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff',
          },
        });
        markersAdded.current = true;
      }
    };

    if (m.loaded()) {
      updateMarkers();
    } else {
      m.once('load', updateMarkers);
    }
  }, [origin, destination]);

  // Pan map when centerOn changes (from geocode select or current location)
  useEffect(() => {
    if (!map.current || !centerOn) return;
    const m = map.current;
    const doPan = () => {
      m.flyTo({ center: [centerOn.lon, centerOn.lat], zoom: Math.max(m.getZoom(), 14), duration: 800 });
    };
    if (m.loaded()) {
      doPan();
    } else {
      m.once('load', doPan);
    }
  }, [centerOn]);

  return (
    <div className="map-container">
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
      <div style={{
        position: 'absolute',
        top: 8,
        left: 8,
        background: 'rgba(22, 33, 62, 0.85)',
        padding: '4px 8px',
        borderRadius: 4,
        fontSize: 11,
        color: '#a0a0c0',
      }}>
        {lat.toFixed(4)}, {lng.toFixed(4)} | zoom: {zoom}
      </div>
      <div style={{
        position: 'absolute',
        top: 8,
        right: 8,
        display: 'flex',
        gap: 2,
        background: 'rgba(22, 33, 62, 0.85)',
        borderRadius: 4,
        overflow: 'hidden',
      }}>
        <button
          onClick={() => switchStyle('topo')}
          style={{
            padding: '4px 8px',
            fontSize: 11,
            border: 'none',
            cursor: 'pointer',
            background: mapStyle === 'topo' ? '#0f3460' : 'transparent',
            color: mapStyle === 'topo' ? '#fff' : '#a0a0c0',
          }}
        >
          Topo
        </button>
        <button
          onClick={() => switchStyle('street')}
          style={{
            padding: '4px 8px',
            fontSize: 11,
            border: 'none',
            cursor: 'pointer',
            background: mapStyle === 'street' ? '#0f3460' : 'transparent',
            color: mapStyle === 'street' ? '#fff' : '#a0a0c0',
          }}
        >
          Street
        </button>
      </div>
    </div>
  );
}